import React, { useState } from "react";
import {
  ModalCheck,
  ModalField,
  ModalFields,
  ModalForm,
  ModalInput,
} from "../../../design-system/patterns/modal-form";

/**
 * Bank details, one of the nine modal forms.
 *
 * Every validation rule, every payload key and the `is_default` to `is_primary`
 * mapping are unchanged. What went is the hand-rolled `.bank-form-group` stack,
 * the `.bank-error-text` span, the fake `.checkmark` checkbox and the 200 line
 * stylesheet that drew all three.
 *
 * PRIVACY. An account number is entered here and never echoed anywhere else.
 * Nothing in this file logs the form state, puts it in a URL or reflects it
 * back into a title, and the list that renders saved accounts shows only the
 * last four digits. Keep it that way.
 */

const emptyState = (initial) => ({
  account_holder_name: initial?.account_holder_name || "",
  account_number: initial?.account_number || "",
  bank_name: initial?.bank_name || "",
  branch_name: initial?.branch_name || initial?.branch_code || "",
  ibn_number: initial?.ibn_number || "",
  is_default: initial?.is_default || initial?.is_primary || false,
});

const BankDetailsForm = ({
  open = true,
  initial = null,
  submitting = false,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState(() => emptyState(initial));
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.account_holder_name.trim()) {
      newErrors.account_holder_name = "Account holder name is required";
    }

    if (!formData.account_number.trim()) {
      newErrors.account_number = "Account number is required";
    } else if (!/^\d+$/.test(formData.account_number)) {
      newErrors.account_number = "Account number must contain only digits";
    }

    if (!formData.bank_name.trim()) {
      newErrors.bank_name = "Bank name is required";
    }

    if (!formData.branch_name.trim()) {
      newErrors.branch_name = "Branch name is required";
    }

    if (!formData.ibn_number.trim()) {
      newErrors.ibn_number = "IBN number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const payload = {
        ...formData,
        // Map UI default flag to backend-required primary flag
        is_primary: !!formData.is_default,
        ...(initial?.id && { bank_account_id: initial.id }),
        ...(initial?.bank_account_id && { bank_account_id: initial.bank_account_id }),
      };
      onSubmit(payload);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <ModalForm
      open={open}
      title={initial ? "Edit payment method" : "Add payment method"}
      onClose={onCancel}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={initial ? "Update payment method" : "Add payment method"}
      submittingLabel="Saving"
    >
      <ModalFields cols={2}>
        <ModalField
          label="Account holder name"
          error={errors.account_holder_name}
          full
        >
          <ModalInput
            value={formData.account_holder_name}
            onChange={(e) => handleChange("account_holder_name", e.target.value)}
            placeholder="As it appears on the account"
            autoComplete="off"
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="Bank name" error={errors.bank_name} full>
          <ModalInput
            value={formData.bank_name}
            onChange={(e) => handleChange("bank_name", e.target.value)}
            placeholder="Enter bank name"
            autoComplete="off"
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="Account number" error={errors.account_number}>
          <ModalInput
            value={formData.account_number}
            onChange={(e) => handleChange("account_number", e.target.value)}
            placeholder="Digits only"
            inputMode="numeric"
            autoComplete="off"
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="IBN number" error={errors.ibn_number}>
          <ModalInput
            value={formData.ibn_number}
            onChange={(e) => handleChange("ibn_number", e.target.value)}
            placeholder="Enter IBN number"
            autoComplete="off"
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="Branch name" error={errors.branch_name} full>
          <ModalInput
            value={formData.branch_name}
            onChange={(e) => handleChange("branch_name", e.target.value)}
            placeholder="Enter branch name"
            autoComplete="off"
            disabled={submitting}
          />
        </ModalField>
      </ModalFields>

      <ModalCheck
        checked={formData.is_default}
        onChange={(e) => handleChange("is_default", e.target.checked)}
        disabled={submitting}
      >
        Use this account for payouts by default
      </ModalCheck>
    </ModalForm>
  );
};

export default BankDetailsForm;
